'use client';

import React, { useState, useEffect } from 'react';
import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { BannerData } from '../types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { GoodListProduct } from './GoodList';
import BannerProductCard from './BannerProductCard';
import { useFrenchSummerTracking } from '../hooks/useFrenchSummerTracking';

import { useInView } from 'react-intersection-observer';

interface BannerDataObj {
  list: BannerData[];
}

interface FrenchSummerBannerProps {
  data: BannerData[] | BannerDataObj | any;
  deviceType: EPlatform;
  allProducts: GoodListProduct[];
  pcAspectRatio?: string;
  wapAspectRatio?: string;
}

const getBannerJumpInfo = (item: BannerData) => {
  const banner = item as BannerData & {
    jump_type?: number | string;
    jump_value?: number | string;
    jump_content?: {
      Jump_target?: number | string;
      jump_target?: number | string;
      jump_value?: number | string;
    };
  };

  return {
    jumpType: `${banner.jump_type ?? banner.type ?? ''}`,
    jumpContent: `${
      banner.jump_value ??
      banner.jump_content?.Jump_target ??
      banner.jump_content?.jump_target ??
      banner.jump_content?.jump_value ??
      banner.shop_sku ??
      ''
    }`,
  };
};

export default function FrenchSummerBanner({
  data,
  deviceType,
  allProducts,
  pcAspectRatio,
  wapAspectRatio,
}: FrenchSummerBannerProps) {
  const isWeb = deviceType === EPlatform.WEB;
  const router = useRouter();
  const [activeSkuIndex, setActiveSkuIndex] = useState<number | null>(null);
  const [wapActiveSkuIndex, setWapActiveSkuIndex] = useState<number | null>(null);
  const { trackAdClick, trackAdExp } = useFrenchSummerTracking();

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && data && data.length > 0) {
      data.forEach((item: BannerData, index: number) => {
        const { jumpType, jumpContent } = getBannerJumpInfo(item);
        trackAdExp('Banner图', 'Banner', index, jumpType, jumpContent);
      });
    }
  }, [inView, data, trackAdExp]);

  if (!data || data.length === 0) return null;

  return (
    <div ref={ref} className="w-full relative bg-[#f5f5f5]">
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        loop={data.length > 1}
        className="w-full"
      >
        {data.map((item, index) => {
          const imageResource = isWeb ? item.pc_image : item.wap_image;
          if (!imageResource) return null;

          const product = allProducts.find((p) => p.shop_sku === item.shop_sku);
          const showCard = isWeb ? activeSkuIndex === index : wapActiveSkuIndex === index;

          // 统一使用全局的 aspectRatio，不再从子项(imageResource.aspectRatio)中获取
          const aspectRatio = isWeb
            ? pcAspectRatio
              ? pcAspectRatio.replace('/', ' / ')
              : '1920 / 600'
            : wapAspectRatio
              ? wapAspectRatio.replace('/', ' / ')
              : '750 / 920';

          return (
            <SwiperSlide key={index}>
              <div
                className="w-full relative cursor-pointer"
                style={{
                  aspectRatio,
                }}
                onMouseEnter={() => isWeb && setActiveSkuIndex(index)}
                onMouseLeave={() => isWeb && setActiveSkuIndex(null)}
                onClick={() => {
                  const { jumpType, jumpContent } = getBannerJumpInfo(item);

                  trackAdClick('Banner图', 'Banner', index, jumpType, jumpContent);

                  if (isWeb) {
                    if (product?.shop_sku) {
                      router.push(`/goods-detail/${product.shop_sku}`);
                    }
                  } else {
                    if (wapActiveSkuIndex === index) {
                      if (product?.shop_sku) {
                        router.push(`/goods-detail/${product.shop_sku}`);
                      }
                    } else {
                      setWapActiveSkuIndex(index);
                    }
                  }
                }}
              >
                <Image
                  src={imageResource}
                  alt={item.image_alt || 'banner'}
                  fill
                  className="object-cover"
                  priority={index === 0}
                />

                {/* 文字内容 */}
                <div className="absolute inset-0 flex flex-col items-center justify-end pointer-events-none px-4 pb-[80px] md:pb-[70px]">
                  {item.title && (
                    <h2
                      className="font-bb text-[32px] md:text-[40px] leading-[1.2] text-center px-4"
                      style={{ color: item.title.color || '#000' }}
                      dangerouslySetInnerHTML={{ __html: item.title.text }}
                    />
                  )}
                  {item.subtitle && (
                    <p
                      className="text-[12px] md:text-[16px] mt-2 text-center px-4"
                      style={{ color: item.subtitle.color || '#000' }}
                      dangerouslySetInnerHTML={{ __html: item.subtitle.text }}
                    />
                  )}
                </div>

                {/* 商品卡片弹窗 */}
                {product && showCard && (
                  <div
                    className={`absolute z-10 shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-opacity duration-300 ${
                      isWeb
                        ? 'right-[60px] bottom-[60px] w-[378px] rounded-[12px]'
                        : 'top-[16px] right-[16px] w-[189px] rounded-[6px]'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Link
                      href={`/goods-detail/${product.shop_sku}`}
                      className="block w-full h-full"
                    >
                      <BannerProductCard product={product} deviceType={deviceType} />
                    </Link>
                  </div>
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
